/** Standard clothing sizes in ascending order. */
export const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL", "XXXXXL"];

/** Size presets per size_system — used by Item Form's "quick add" chips. */
export const SIZE_PRESETS = {
    clothing: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
    shoe_in: ["4", "5", "6", "7", "8", "9", "10"],
    shoe_uk: ["3", "4", "5", "6", "7", "8", "9"],
    shoe_eu: ["36", "37", "38", "39", "40", "41", "42"],
    numeric: ["26", "28", "30", "32", "34", "36", "38"],
    none: [],
};

/** Friendly label for a size_system. */
export const SIZE_SYSTEM_LABEL = {
    clothing: "Clothing sizes",
    shoe_in: "Footwear · IN",
    shoe_uk: "Footwear · UK",
    shoe_eu: "Footwear · EU",
    numeric: "Numeric",
    none: "One size",
};

/**
 * Derive the **display** size list for an item.
 *   1) Prefer per-size stock keys (most accurate).
 *   2) Fall back to the legacy `sizes` text field.
 *   3) Returns [] when no sizing data is available.
 *
 * This is what the customer item-detail page should use to render the size
 * pills, since some legacy items have `size_stock` but no `sizes` string.
 */
export function getDisplaySizes(item) {
    if (!item) return [];
    const ss = item.size_stock;
    if (ss && typeof ss === "object" && Object.keys(ss).length > 0) {
        const keys = Object.keys(ss).map((s) => String(s).trim().toUpperCase()).filter(Boolean);
        // "FREE" is the internal sentinel the admin form writes for SIZELESS
        // categories (jewellery, bags, accessories). It is stock, not a
        // selectable size — so a FREE-only item has NO display sizes. This
        // keeps the PDP free of size chips / size-chart / fit copy for those
        // verticals while leaving real sized (clothing) items untouched.
        const nonFree = keys.filter((k) => k !== "FREE");
        if (nonFree.length === 0) return [];
        return nonFree.sort((a, b) => {
            const ia = SIZE_ORDER.indexOf(a);
            const ib = SIZE_ORDER.indexOf(b);
            if (ia === -1 && ib === -1) return 0;
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
        });
    }
    return parseSizes(item.sizes);
}


/**
 * Parse a free-form sizes string into a clean array of size tokens.
 *   "S to L"        -> ["S", "M", "L"]
 *   "M, XL, XXL"    -> ["M", "XL", "XXL"]
 *   "S TO XL"       -> ["S", "M", "L", "XL"]
 *   "Free Size"     -> ["Free Size"]
 *   "L - XXL"       -> ["L", "XL", "XXL"]
 *
 * Standard tokens are returned uppercase and sorted by SIZE_ORDER.
 * Non-standard tokens (e.g. "Free Size") are preserved as-is and pushed to the end.
 */
export function parseSizes(raw) {
    if (!raw) return [];
    const out = [];
    const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean);

    for (const p of parts) {
        const up = p.toUpperCase();
        const range = up.match(/^([A-Z]+)\s*(?:TO|->|—|–|-)\s*([A-Z]+)$/);
        if (range && SIZE_ORDER.includes(range[1]) && SIZE_ORDER.includes(range[2])) {
            const i1 = SIZE_ORDER.indexOf(range[1]);
            const i2 = SIZE_ORDER.indexOf(range[2]);
            const [lo, hi] = i1 <= i2 ? [i1, i2] : [i2, i1];
            for (let i = lo; i <= hi; i++) out.push(SIZE_ORDER[i]);
        } else {
            out.push(SIZE_ORDER.includes(up) ? up : p);
        }
    }

    // Dedupe (preserve first occurrence)
    const seen = new Set();
    const deduped = out.filter((s) => (seen.has(s) ? false : (seen.add(s), true)));

    // Standard first (sorted), non-standard after
    return deduped.sort((a, b) => {
        const ia = SIZE_ORDER.indexOf(a.toUpperCase());
        const ib = SIZE_ORDER.indexOf(b.toUpperCase());
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });
}
